import { createSlice } from '@reduxjs/toolkit';
import { request } from 'modules/Api/HttpClient';
import {
  AUTH_LOGIN_URL,
  AUTH_FORGOT_PASSWORD_URL,
  AUTH_REQUEST_FORGOT_PASSWORD_URL,
  AUTH_2FA_SETUP_URL,
  AUTH_2FA_VALIDATE_URL,
  AUTH_2FA_EMAIL_SEND_URL,
  AUTH_2FA_EMAIL_VERIFY_URL,
  PERMISSIONS_CHECK_URL,
  SETTINGS_PROFILE_UPDATE_URL,
  SETTINGS_REMOVE_AVATAR_URL,
  SETTINGS_AVATAR_BASE64_URL,
  SETTINGS_2FA_URL,
  SETTINGS_2FA_SETUP_URL,
  SETTINGS_2FA_EMAIL_SEND_URL,
  SETTINGS_2FA_EMAIL_VERIFY_URL,
} from 'modules/Api/Routes';
import { generatePayload } from 'modules/Api/Payload';
import { formDataFromObj } from 'modules/Api/RequestData';
import {
  persistSession,
  getSession,
  cleanSession,
  persistTrustedDeviceToken,
  getTrustedDeviceToken,
} from 'modules/Auth/Services';
import {
  isSuperAdmin,
  isGroupAdmin,
  isUnitAdmin,
  isSchoolAdmin,
  isAdmin,
  isEvaluator,
} from 'modules/Users/Permissions/UserType';

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: null,
    /** handle initial load */
    sessionLoaded: false,
    loading: false,
    forgotStatus: null,
    tokenExpired: false,
    serverError: '',
    twoFactor: null,
  },
  reducers: {
    setAuthData: (state, action) => {
      state.sessionLoaded = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.permissions = null;
    },
    updateUserData: (state, action) => {
      state.user = action.payload.user;
    },
    setPermissions: (state, action) => {
      state.permissions = action.payload.content;
      state.loading = false;
    },
    setPermissionsError: (state) => {
      state.loading = false;
    },
    setServerError: (state, action) => {
      state.loading = false;
      state.serverError = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setTokenExpired: (state, action) => {
      state.tokenExpired = action.payload.expired;
      if (action.payload.currentUrl) {
        state.lastPageAccessed = action.payload.currentUrl;
      }
    },
    clearTokenExpired: (state) => {
      state.tokenExpired = false;
      state.lastPageAccessed = null;
    },
    setTokenInvalid: (state) => {
      state.user = null;
      state.token = null;
      cleanSession();
    },
    clearServerError: (state) => {
      state.serverError = '';
    },
    setTwoFactor: (state, action) => {
      state.twoFactor = action.payload;
      state.loading = false;
    },
    clearTwoFactor: (state) => {
      state.twoFactor = null;
    },
    setForgotStatus: (state, action) => {
      state.forgotStatus = action.payload;
    },
    setAvatarBase64: (state, action) => {
      if (state.user) {
        state.user.avatar_base64 = action.payload;
      }
    },
  },
});

const Actions = authSlice.actions;

const Selectors = {
  selectAuthData: (state) => ({
    user: state.auth.user,
    token: state.auth.token,
  }),

  getPermissions: (state) => state?.auth?.permissions,

  getServerError: ({ auth: { serverError } }) => serverError,

  getExpiredToken: (state) => state?.auth?.tokenExpired,

  sessionLoaded: (state) => state?.auth?.sessionLoaded,

  selectLoading: (state) => state?.auth?.loading,

  isTokenExpired: (state) => state?.auth?.tokenExpired,

  getLastPageAccessed: (state) => state?.auth?.lastPageAccessed,

  forgotHandling: ({ auth: { loading, forgotStatus } }) => ({
    loading,
    forgotStatus,
  }),

  authLoading: ({ auth: { loading } }) => ({ loading }),

  isSuperAdmin: (state) => isSuperAdmin(state?.auth?.user?.user_type),

  isGroupAdmin: (state) => isGroupAdmin(state?.auth?.user?.user_type),

  isUnitAdmin: (state) => isUnitAdmin(state?.auth?.user?.user_type),

  isSchoolAdmin: (state) => isSchoolAdmin(state?.auth?.user?.user_type),

  isAdmin: (state) => isAdmin(state?.auth?.user?.user_type),

  isOxfordAdmin: (state) => state?.auth?.user?.oxford_unit_admin,

  isEvaluator: (state) => isEvaluator(state?.auth?.user?.user_type),

  timezone: (state) => state?.auth?.user?.timezone,

  timeFormat: (state) => state?.auth?.user?.time_format,

  dateFormat: (state) => state?.auth?.user?.date_format,

  dashboardWidgets: (state) => state?.auth?.user?.dashboard,

  canEvaluate: (state) =>
    isSuperAdmin(state?.auth?.user?.user_type) ||
    !!state?.auth?.user?.can_evaluate,

  canDeleteLicense: (state) =>
    isSuperAdmin(state?.auth?.user?.user_type) ||
    !!state?.auth?.user?.can_delete_license ||
    !!state?.auth?.user?.can_delete_student_license,

  canChangeEvaluators: (state) =>
    isSuperAdmin(state?.auth?.user?.user_type) ||
    !!state?.auth?.user?.can_cancel_interview,

  twoFactor: (state) => state?.auth?.twoFactor,
};

const Async = {
  /**
   * @param {Object} data
   * @param {String} data.email
   * @param {String} data.password
   * @param {Function} onError
   */
  signIn: (data, onError, onSuccess) => async (dispatch) => {
    dispatch(Actions.setLoading(true));

    try {
      const response = await request({
        method: 'POST',
        url: AUTH_LOGIN_URL,
        data: {
          payload: generatePayload({
            ...data,
            trusted_device_token: getTrustedDeviceToken(),
          }),
        },
      });

      const content = response.data.content;

      if (content.require_2fa_setup || content.require_2fa_verification) {
        dispatch(
          Actions.setTwoFactor({
            step: content.require_2fa_setup ? 'setup' : 'verify',
            partialToken: content.partial_token,
            availableMethods: content.available_methods || [],
            method: content.two_factor_method || null,
          })
        );
        return;
      }

      const { user } = content;
      const { token } = user;

      delete user.token;

      dispatch(Actions.setAuthData({ user, token }));
      dispatch(Actions.clearTokenExpired());
      dispatch(Async.checkPermissions());
      persistSession(user, token);
      onSuccess && onSuccess();
    } catch (e) {
      dispatch(Actions.setLoading(false));
      onError(e);
    }
  },

  /**
   * @param {Object} data
   * @param {String} data.email
   * @param {Function} onError
   */
  forgotPassword: (data, onError) => async (dispatch) => {
    dispatch(Actions.setLoading(true));

    try {
      await request({
        method: 'POST',
        url: AUTH_REQUEST_FORGOT_PASSWORD_URL,
        data,
      });

      dispatch(Actions.setForgotStatus('success'));
    } catch (e) {
      onError(e);
    } finally {
      dispatch(Actions.setLoading(false));
    }
  },

  /**
   * @param {Object} data
   * @param {String} data.token
   * @param {String} data.password
   * @param {Function} onSuccess
   * @param {Function} onError
   */
  newPassword:
    ({ token, password }, onSuccess, onError) =>
    async (dispatch) => {
      dispatch(Actions.setLoading(true));

      try {
        await request({
          method: 'POST',
          url: AUTH_FORGOT_PASSWORD_URL,
          data: { forgot_password_token: token, password },
        });
        onSuccess();
      } catch (e) {
        onError(e);
      } finally {
        dispatch(Actions.setLoading(false));
      }
    },

  twoFactorGetSetup: (partialToken, onError) => async () => {
    try {
      const response = await request({
        method: 'GET',
        url: AUTH_2FA_SETUP_URL,
        headers: { Authorization: `Bearer ${partialToken}` },
      });
      return response.data.content;
    } catch (e) {
      onError && onError(e);
      return null;
    }
  },

  twoFactorConfirmSetup:
    (partialToken, code, rememberDevice, onError, onSuccess) =>
    async (dispatch) => {
      dispatch(Actions.setLoading(true));
      try {
        const response = await request({
          method: 'POST',
          url: AUTH_2FA_SETUP_URL,
          data: { code, remember_device: !!rememberDevice },
          headers: { Authorization: `Bearer ${partialToken}` },
        });
        const { user } = response.data.content;
        const { token } = user;
        delete user.token;
        dispatch(Actions.clearTwoFactor());
        dispatch(Actions.setAuthData({ user, token }));
        dispatch(Actions.clearTokenExpired());
        dispatch(Async.checkPermissions());
        persistSession(user, token);
        onSuccess && onSuccess();
      } catch (e) {
        dispatch(Actions.setLoading(false));
        onError && onError(e);
      }
    },

  twoFactorValidate:
    (partialToken, code, rememberDevice, onError, onSuccess) =>
    async (dispatch) => {
      dispatch(Actions.setLoading(true));
      try {
        const response = await request({
          method: 'POST',
          url: AUTH_2FA_VALIDATE_URL,
          data: { code, remember_device: !!rememberDevice },
          headers: { Authorization: `Bearer ${partialToken}` },
        });
        const { user, trusted_device_token } = response.data.content;
        const { token } = user;
        delete user.token;
        dispatch(Actions.clearTwoFactor());
        dispatch(Actions.setAuthData({ user, token }));
        dispatch(Actions.clearTokenExpired());
        dispatch(Async.checkPermissions());
        persistSession(user, token);
        if (trusted_device_token)
          persistTrustedDeviceToken(trusted_device_token);
        onSuccess && onSuccess();
      } catch (e) {
        dispatch(Actions.setLoading(false));
        onError && onError(e);
      }
    },

  twoFactorSendEmail: (partialToken, onError) => async () => {
    try {
      await request({
        method: 'POST',
        url: AUTH_2FA_EMAIL_SEND_URL,
        headers: { Authorization: `Bearer ${partialToken}` },
      });
    } catch (e) {
      onError && onError(e);
    }
  },

  twoFactorVerifyEmail:
    (partialToken, code, rememberDevice, onError, onSuccess) =>
    async (dispatch) => {
      dispatch(Actions.setLoading(true));
      try {
        const response = await request({
          method: 'POST',
          url: AUTH_2FA_EMAIL_VERIFY_URL,
          data: { code, remember_device: !!rememberDevice },
          headers: { Authorization: `Bearer ${partialToken}` },
        });
        const { user, trusted_device_token } = response.data.content;
        const { token } = user;
        delete user.token;
        dispatch(Actions.clearTwoFactor());
        dispatch(Actions.setAuthData({ user, token }));
        dispatch(Actions.clearTokenExpired());
        dispatch(Async.checkPermissions());
        persistSession(user, token);
        if (trusted_device_token)
          persistTrustedDeviceToken(trusted_device_token);
        onSuccess && onSuccess();
      } catch (e) {
        dispatch(Actions.setLoading(false));
        onError && onError(e);
      }
    },

  checkPermissions: () => async (dispatch) => {
    try {
      const response = await request({
        method: 'GET',
        url: PERMISSIONS_CHECK_URL,
      });
      dispatch(Actions.setPermissions(response.data));
      dispatch(Actions.clearServerError());
    } catch (e) {
      dispatch(Actions.setPermissionsError(e.message));
    }
  },

  profileDisable2FA:
    ({ onSuccess, onError }) =>
    async (dispatch) => {
      try {
        const response = await request({
          method: 'DELETE',
          url: SETTINGS_2FA_URL,
        });
        const { user } = response.data.content;
        const session = getSession();
        dispatch(Actions.setAuthData({ user, token: session?.token }));
        persistSession(user, session?.token);
        onSuccess && onSuccess();
      } catch (e) {
        onError && onError(e);
      }
    },

  profileGetSetup2FA:
    ({ onSuccess, onError }) =>
    async () => {
      try {
        const response = await request({
          method: 'GET',
          url: SETTINGS_2FA_SETUP_URL,
        });
        onSuccess && onSuccess(response.data.content);
      } catch (e) {
        onError && onError(e);
      }
    },

  profileConfirmSetup2FA:
    ({ code, onSuccess, onError }) =>
    async (dispatch) => {
      try {
        const response = await request({
          method: 'POST',
          url: SETTINGS_2FA_SETUP_URL,
          data: { code },
        });
        const { user } = response.data.content;
        const session = getSession();
        dispatch(Actions.setAuthData({ user, token: session?.token }));
        persistSession(user, session?.token);
        onSuccess && onSuccess();
      } catch (e) {
        onError && onError(e);
      }
    },

  profileSendEmail2FA:
    ({ onSuccess, onError }) =>
    async () => {
      try {
        await request({ method: 'POST', url: SETTINGS_2FA_EMAIL_SEND_URL });
        onSuccess && onSuccess();
      } catch (e) {
        onError && onError(e);
      }
    },

  profileVerifyEmail2FA:
    ({ code, onSuccess, onError }) =>
    async (dispatch) => {
      try {
        const response = await request({
          method: 'POST',
          url: SETTINGS_2FA_EMAIL_VERIFY_URL,
          data: { code },
        });
        const { user } = response.data.content;
        const session = getSession();
        dispatch(Actions.setAuthData({ user, token: session?.token }));
        persistSession(user, session?.token);
        onSuccess && onSuccess();
      } catch (e) {
        onError && onError(e);
      }
    },

  logout: () => (dispatch) => {
    cleanSession();
    sessionStorage.removeItem('redirectAfterLogin');
    sessionStorage.setItem('userLoggedOut', 'true');
    dispatch(Actions.setAuthData({ user: null, token: null }));
  },

  hydrateSession: () => async (dispatch) => {
    const session = getSession();

    dispatch(
      Actions.setAuthData(
        session || {
          user: null,
          token: null,
        }
      )
    );
  },

  updateProfile:
    ({ data, onSuccess, onError }) =>
    async (dispatch, getState) => {
      dispatch(Actions.setLoading(true));

      const {
        auth: { token },
      } = getState();

      let formattedIntervals = data?.intervals?.map((item) => {
        const interval = {
          start_date: item.start_date,
          end_date: item.end_date,
          start_time: item.start_time,
          end_time: item.end_time,
        };

        item?.id && (interval.id = item?.id);

        return interval;
      });

      if (data?.removedIntervals)
        formattedIntervals = [...formattedIntervals, ...data.removedIntervals];

      const requestData = formDataFromObj({
        ...data,
        intervals: formattedIntervals
          ? JSON.stringify(formattedIntervals)
          : null,
      });

      try {
        const response = await request({
          method: 'PUT',
          url: SETTINGS_PROFILE_UPDATE_URL,
          data: requestData,
        });
        const updatedUser = response?.data?.content;
        dispatch(Actions.updateUserData({ user: updatedUser }));
        persistSession(updatedUser, token);
        onSuccess(response);
      } catch (e) {
        onError(e);
      } finally {
        dispatch(Actions.setLoading(false));
      }
    },

  updateEvaluatorProfile:
    ({ data, onSuccess, onError }) =>
    async (dispatch, getState) => {
      dispatch(Actions.setLoading(true));

      const {
        auth: { token },
      } = getState();

      try {
        if (!(data.avatar instanceof File)) delete data.avatar;

        const formattedIntervals = data?.intervals
          ?.filter((interval) => interval?.start_date)
          ?.map((interval) => {
            const base = {
              end_date: interval?.end_date,
              end_time: interval?.end_time,
              start_date: interval?.start_date,
              start_time: interval?.start_time,
            };
            interval?.id && (base.id = interval?.id);
            return base;
          });

        const evaluatorData = formDataFromObj({
          ...data,
          intervals: JSON.stringify([
            ...formattedIntervals,
            ...data.removedIntervals,
          ]),
        });

        const response = await request({
          method: 'PUT',
          url: SETTINGS_PROFILE_UPDATE_URL,
          data: evaluatorData,
        });
        const updatedUser = response?.data?.content;
        dispatch(Actions.setAuthData({ user: updatedUser, token }));
        persistSession(updatedUser, token);
        onSuccess(response);
      } catch (e) {
        onError(e);
      } finally {
        dispatch(Actions.setLoading(false));
      }
    },

  removeAvatar:
    ({ id, onSuccess, onError }) =>
    async (dispatch, getState) => {
      dispatch(Actions.setLoading(true));

      const {
        auth: { token },
      } = getState();

      try {
        const data = formDataFromObj({ id });

        const response = await request({
          method: 'PUT',
          url: SETTINGS_REMOVE_AVATAR_URL,
          data,
        });
        const updatedUser = response?.data?.content;
        dispatch(Actions.updateUserData({ user: updatedUser }));
        persistSession(updatedUser, token);
        onSuccess(response);
      } catch (e) {
        onError(e);
      } finally {
        dispatch(Actions.setLoading(false));
      }
    },

  getAvatarBase64:
    ({ onSuccess, onError }) =>
    async (dispatch) => {
      try {
        const response = await request({
          method: 'GET',
          url: SETTINGS_AVATAR_BASE64_URL,
        });
        const avatar = response.data.content.avatar;
        dispatch(Actions.setAvatarBase64(avatar));
        onSuccess && onSuccess(avatar);
      } catch (e) {
        onError && onError(e);
      }
    },
};

const { reducer } = authSlice;

export { reducer, authSlice, Async, Actions, Selectors };
